import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import {
  formatHhmm,
  parseHhmm,
  type DayInterval,
  type ForwardingSettings,
  type WeeklyWindow,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { forwardingErrorMessage } from "@/lib/forwarding";
import { useSaveForwardingSettings } from "@/lib/hooks/useForwarding";
import { cn } from "@/lib/utils";
import { InlineError, Segmented, Toggle } from "./controls";

const DAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const DEFAULT_INTERVAL: DayInterval = { start: 8 * 60, end: 22 * 60 };
const timeInputClass =
  "h-8 w-[5.5rem] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--input))] px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40";

interface WindowCardProps {
  readonly settings: ForwardingSettings;
}

function DayRow({
  day,
  interval,
  onChange,
}: {
  day: number;
  interval: DayInterval | null;
  onChange: (i: DayInterval | null) => void;
}): JSX.Element {
  const update = (field: "start" | "end", value: string): void => {
    const minutes = parseHhmm(value);
    if (minutes === null || !interval) return;
    onChange({ ...interval, [field]: minutes });
  };
  const invalid = interval !== null && interval.start >= interval.end;
  return (
    <div className="flex flex-wrap items-center gap-3 py-1.5">
      <label className="flex w-28 items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[hsl(var(--primary))]"
          checked={interval !== null}
          onChange={(e) => onChange(e.target.checked ? DEFAULT_INTERVAL : null)}
        />
        {DAYS[day]}
      </label>
      <input
        type="time"
        aria-label={`Início ${DAYS[day]}`}
        className={timeInputClass}
        disabled={!interval}
        value={interval ? formatHhmm(interval.start) : ""}
        onChange={(e) => update("start", e.target.value)}
      />
      <span className="text-xs text-muted-foreground">até</span>
      <input
        type="time"
        aria-label={`Fim ${DAYS[day]}`}
        className={cn(timeInputClass, invalid && "border-destructive")}
        disabled={!interval}
        value={interval ? formatHhmm(Math.min(interval.end, 1439)) : ""}
        onChange={(e) => update("end", e.target.value)}
      />
      {!interval && <span className="text-xs text-muted-foreground">sem envios</span>}
      {invalid && <span className="text-xs text-destructive">início precisa ser antes do fim</span>}
    </div>
  );
}

function WindowHeader(): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <CalendarClock size={17} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id="window-title" className="font-semibold text-foreground">
          Agenda dos envios
        </h2>
        <p className="text-xs text-muted-foreground">
          Dias e horários em que o encaminhamento posta, no horário de Brasília.
        </p>
      </div>
    </div>
  );
}

function ScheduleEditor({
  draft,
  onChange,
}: {
  draft: WeeklyWindow;
  onChange: (next: WeeklyWindow) => void;
}): JSX.Element {
  return (
    <>
      <div className="divide-y divide-border/60">
        {draft.schedule.map((interval, day) => (
          <DayRow
            key={day}
            day={day}
            interval={interval}
            onChange={(next) =>
              onChange({
                ...draft,
                schedule: draft.schedule.map((v, i) => (i === day ? next : v)),
              })
            }
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-foreground">Ofertas fora do horário:</span>
        <Segmented
          label="Ofertas fora do horário"
          value={draft.outside}
          onChange={(outside) => onChange({ ...draft, outside })}
          options={[
            { value: "drop", label: "Descartar" },
            { value: "queue", label: "Enviar na próxima abertura" },
          ]}
        />
      </div>
    </>
  );
}

/** Card da agenda semanal (fuso de Brasília). */
export function WindowCard({ settings }: WindowCardProps): JSX.Element {
  const [draft, setDraft] = useState<WeeklyWindow>(settings.window);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveForwardingSettings();
  useEffect(() => setDraft(settings.window), [settings.window]);

  const invalid = draft.schedule.some((i) => i !== null && i.start >= i.end);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.window);
  const onSave = async (): Promise<void> => {
    setError(null);
    try {
      await save.mutateAsync({ ...settings, window: draft });
    } catch (err) {
      setError(forwardingErrorMessage(err));
    }
  };

  return (
    <section
      className="space-y-4 rounded-xl border border-border bg-card p-5"
      aria-labelledby="window-title"
    >
      <WindowHeader />
      <Toggle
        id="window-enabled"
        checked={draft.enabled}
        onChange={(enabled) => setDraft({ ...draft, enabled })}
        label="Limitar a uma agenda"
      />
      {draft.enabled && <ScheduleEditor draft={draft} onChange={setDraft} />}
      <InlineError message={error} />
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!dirty || invalid || save.isPending}
          onClick={() => void onSave()}
        >
          {save.isPending ? "Salvando..." : "Salvar agenda"}
        </Button>
      </div>
    </section>
  );
}
